import {
  createAsyncMessageBundleManager,
  LocalizationContextBuilder,
  type AsyncMessageBundleManager,
} from '@amzn/arb-tools';
import arbManifest from 'translations/arbManifest.json';
import translationUnd from 'translations/translation.json';
import translationEn from 'translations/translation-en.json';
import translationDe from 'translations/translation-de.json';
import translationEs from 'translations/translation-es.json';
import translationFr from 'translations/translation-fr.json';
import translationIt from 'translations/translation-it.json';
import translationJa from 'translations/translation-ja.json';
import translationPl from 'translations/translation-pl.json';
import translationPt from 'translations/translation-pt.json';
import { DEFAULT_LOCALE } from './locales';

/**
 * Source-locale (`und`) fallback pack. Exported so `resolveResourcePack`
 * can degrade to it when the manifest references a file we haven't
 * inlined yet — kept as a named export for tests to assert the fallback
 * identity without duplicating the import.
 */
export const SOURCE_LOCALE_PACK: unknown = translationUnd;

/**
 * Inlined resource packs, keyed by the file name that `arb-tools compile`
 * writes into `public/translations/` and references from
 * `arbManifest.endpointMap`.
 *
 * arb-tools resolves a message by reading `endpointMap[locale][bundle]`
 * to get a file name, then calling `resolveBundleUrl` and
 * `resolveResourcePack` to turn that file name into JSON.
 *
 * We inline instead of fetching because Katal's dev-server middleware
 * 500s any request that isn't in the Katal manifest, so files under
 * `public/translations/` aren't reachable in dev. Inlining also
 * collapses to a single network payload in prod.
 *
 * When Panther produces additional per-locale packs, add one import +
 * one map entry here. The rest of the pipeline picks them up
 * automatically because `arbManifest.endpointMap` gains a matching
 * locale entry at compile time.
 */
export const RESOURCE_PACKS: ReadonlyMap<string, unknown> = new Map<string, unknown>([
  ['translation.json', translationUnd],
  ['translation-en.json', translationEn],
  ['translation-de.json', translationDe],
  ['translation-es.json', translationEs],
  ['translation-fr.json', translationFr],
  ['translation-it.json', translationIt],
  ['translation-ja.json', translationJa],
  ['translation-pl.json', translationPl],
  ['translation-pt.json', translationPt],
]);

/**
 * Look up a resource pack by the file name that arb-tools' manager
 * resolved from the manifest. Falls back to the source-locale pack when
 * the file name is missing from `RESOURCE_PACKS` — this happens when a
 * new locale gets compiled into `public/translations/` but nobody added
 * the corresponding import above. Returning the source pack means the
 * UI shows English strings instead of crashing at runtime.
 */
export function resolveResourcePack(url: string): Promise<unknown> {
  return Promise.resolve(RESOURCE_PACKS.get(url) ?? SOURCE_LOCALE_PACK);
}

/**
 * Pass the manifest's resource-pack path through unchanged. When the
 * manifest has no entry for the requested `(locale, bundle)` pair, the
 * manager passes `undefined` here and we return `undefined` — that
 * signals arb-tools to fall through to its parent-locale entry
 * (`es-ES` → `es` → `und`).
 */
export function resolveBundleUrl(
  resourcePackPath: string | undefined,
): string | undefined {
  return resourcePackPath;
}

/**
 * Build the AsyncMessageBundleManager for v2.
 *
 * `arb-tools compile` produces `translation.json` (source-locale bundles
 * keyed by `und`) plus one `translation-<locale>.json` per translated
 * locale, plus an `arbManifest.json` that maps
 * `(locale, bundle) → file name`.
 *
 * When the user's locale isn't listed in the manifest, arb-tools'
 * built-in locale fallback (`es-ES` → `es` → `und`) picks the closest
 * available entry, which today is always `und` (English source). Once
 * Panther-translated packs land, the same fallback picks the localized
 * pack instead — no code change required.
 *
 * Why inline instead of fetching:
 *   1. Katal's dev-server middleware intercepts every request and 500s
 *      anything not in the Katal manifest, so static files under
 *      `public/translations/` are unreachable in dev.
 *   2. In prod, inlining collapses two extra network round-trips
 *      (bundle URL lookup + resource pack fetch) into a single JS chunk.
 */
export function createMobileMbm(): AsyncMessageBundleManager {
  const defaultContext = new LocalizationContextBuilder()
    .withLocale(DEFAULT_LOCALE)
    .withDefaultLocale(DEFAULT_LOCALE)
    .build();

  return createAsyncMessageBundleManager({
    arbManifest,
    localizationContextProvider: () => defaultContext,
    resolveBundleUrl,
    resolveResourcePack,
  });
}
